import React from 'react';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';

const CorporateStructurePage = () => {
  const entities = [
    {
      name: 'UE Global Holdings, Inc.',
      jurisdiction: 'Delaware, USA',
      type: 'C-Corporation',
      description: 'Parent holding company for all UE entities worldwide',
      status: 'Active',
    },
    {
      name: 'UE Dominicana, SRL',
      jurisdiction: 'Dominican Republic',
      type: 'Local Subsidiary',
      description: 'Operating entity for Dominican Republic market',
      status: 'Active',
    },
    {
      name: 'UE Puerto Rico, LLC',
      jurisdiction: 'Puerto Rico, USA',
      type: 'Local Subsidiary',
      description: 'Operating entity for Puerto Rico market',
      status: 'Active',
    },
    {
      name: 'UE Colombia (Planned)',
      jurisdiction: 'Colombia',
      type: 'Future Subsidiary',
      description: 'Planned operating entity for Colombia expansion',
      status: 'Planned',
    },
  ];

  const leadership = [
    {
      name: 'CEO',
      title: 'Chief Executive Officer',
      description: 'Strategic vision and company direction',
    },
    {
      name: 'CTO',
      title: 'Chief Technology Officer',
      description: 'Platform development and technical strategy',
    },
    {
      name: 'CFO',
      title: 'Chief Financial Officer',
      description: 'Financial operations and investor relations',
    },
    {
      name: 'COO',
      title: 'Chief Operating Officer',
      description: 'Market operations and expansion',
    },
  ];

  const boardMembers = [
    { role: 'Board Chair', description: 'Independent director with fintech experience' },
    { role: 'Board Member', description: 'Investor representative' },
    { role: 'Board Member', description: 'Industry expert in marketplace businesses' },
    { role: 'Board Observer', description: 'Strategic advisor' },
  ];

  const advisors = [
    { area: 'Technology', expertise: 'Marketplace infrastructure and scaling' },
    { area: 'Finance', expertise: 'VC fundraising and financial strategy' },
    { area: 'Legal', expertise: 'LATAM regulatory compliance' },
    { area: 'Operations', expertise: 'Multi-market expansion' },
  ];

  const partners = [
    { name: 'Stripe', type: 'Payment Infrastructure' },
    { name: 'Azure', type: 'Cloud Services' },
    { name: 'Supabase', type: 'Database Infrastructure' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium text-accent uppercase tracking-wider mb-4">
              Corporate Structure
            </p>
            <h1 className="text-hero text-foreground mb-6">
              Governance & Organization
            </h1>
            <p className="text-body-large">
              Built for transparency, compliance, and scalable growth across multiple jurisdictions.
            </p>
          </div>
        </div>
      </section>

      {/* Corporate Entities */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="mb-12">
            <h2 className="text-display text-foreground mb-4">
              Corporate Entities
            </h2>
            <p className="text-muted-foreground max-w-2xl">
              Our corporate structure is designed for regulatory compliance and operational
              efficiency across all markets we operate in.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {entities.map((entity, index) => (
              <div
                key={index}
                className={`p-6 rounded-brand border ${
                  entity.status === 'Active'
                    ? 'bg-white border-border'
                    : 'bg-white/50 border-dashed border-border'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {entity.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {entity.jurisdiction}
                    </p>
                  </div>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                    entity.status === 'Active'
                      ? 'bg-accent/10 text-accent'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {entity.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Building" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{entity.type}</span>
                </div>
                <p className="text-sm text-foreground">
                  {entity.description}
                </p>
              </div>
            ))}
          </div>

          {/* Future Expansion Note */}
          <div className="mt-8 p-6 bg-primary/5 rounded-brand border border-primary/10">
            <div className="flex items-start gap-4">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Future Subsidiaries</h4>
                <p className="text-sm text-muted-foreground">
                  As we expand into new markets (Colombia, Brazil, Chile, Mexico), we will establish
                  local subsidiaries to ensure regulatory compliance and operational efficiency.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section id="leadership" className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="mb-12">
            <h2 className="text-display text-foreground mb-4">
              Leadership Team
            </h2>
            <p className="text-muted-foreground max-w-2xl">
              Experienced leaders driving our mission to transform the Latin American service economy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {leadership.map((leader, index) => (
              <div key={index} className="p-6 bg-muted rounded-brand">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name="User" size={28} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {leader.name}
                </h3>
                <p className="text-sm text-accent mb-2">{leader.title}</p>
                <p className="text-sm text-muted-foreground">
                  {leader.description}
                </p>
              </div>
            ))}
          </div>

          {/* Board Structure */}
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-6">
                Board of Directors
              </h3>
              <div className="space-y-4">
                {boardMembers.map((member, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-muted rounded-brand">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="UserCheck" size={18} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{member.role}</div>
                      <div className="text-sm text-muted-foreground">{member.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-foreground mb-6">
                Advisory Board
              </h3>
              <div className="space-y-4">
                {advisors.map((advisor, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-muted rounded-brand">
                    <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="Lightbulb" size={18} className="text-secondary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{advisor.area} Advisor</div>
                      <div className="text-sm text-muted-foreground">{advisor.expertise}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnerships & Programs */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Partner Banks */}
            <div className="bg-white rounded-brand p-6 border border-border">
              <Icon name="Building2" size={24} className="text-primary mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Technology Partners
              </h3>
              <div className="space-y-3">
                {partners.map((partner, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <span className="font-medium text-foreground">{partner.name}</span>
                    <span className="text-sm text-muted-foreground">{partner.type}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Internship Programs */}
            <div className="bg-white rounded-brand p-6 border border-border">
              <Icon name="GraduationCap" size={24} className="text-primary mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Internship Programs
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                We partner with universities in the Dominican Republic to provide
                internship opportunities and workforce development.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  INTEC Partnership
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  PUCMM Collaboration
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  UASD Program
                </li>
              </ul>
            </div>

            {/* Government Partnerships */}
            <div className="bg-white rounded-brand p-6 border border-border">
              <Icon name="Landmark" size={24} className="text-primary mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Government Relations
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Working with government agencies to support formalization
                and economic development initiatives.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  MICM Registry Program
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  DGII Tax Compliance
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  ProDominicana Export
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-wide mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-display text-white mb-6">
            Want to learn more?
          </h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Contact our investor relations team for detailed information about
            our corporate structure, governance, and strategic direction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/investor-relations"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary font-medium rounded-brand-sm hover:bg-white/90 transition-colors"
            >
              Investor Relations
              <Icon name="ArrowRight" size={16} />
            </Link>
            <Link
              to="/partnerships"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/30 text-white font-medium rounded-brand-sm hover:bg-white/10 transition-colors"
            >
              Partnership Opportunities
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CorporateStructurePage;
